import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { authAPI, getHomeRoute } from '@/lib/api';
import { enterFullscreenWithEscapeLock } from '@/lib/fullscreenUtils';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Store } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState('Completing sign-in…');

  useEffect(() => {
    const finish = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error || !data.session) {
          setStatus('Sign-in failed. Redirecting…');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        const token = data.session.access_token;
        const response = await authAPI.googleCallback(token);
        const { mfa_required, mfa_enrolled, user: userData } = response.data;

        if (mfa_required) {
          // Supabase client already has the aal1 session from getSession() above — no setSession needed
          sessionStorage.setItem('kiranaflow_pending_user', JSON.stringify(userData));
          // Gate token: MFARoute checks kiranaflow_token — store aal1 token in sessionStorage
          sessionStorage.setItem('kiranaflow_token', response.data.token);
          if (response.data.session_id) localStorage.setItem('kiranaflow_session_id', response.data.session_id);
          navigate(mfa_enrolled ? '/mfa/verify' : '/mfa/enroll', { replace: true });
        } else {
          await login({ ...response.data, refresh_token: data.session.refresh_token });
          toast.success('Signed in with Google!');
          navigate(getHomeRoute(userData?.role));
          enterFullscreenWithEscapeLock();
        }
      } catch (err) {
        const detail = err.response?.data?.detail || 'Google sign-in failed';
        toast.error(detail);
        setStatus(detail + ' Redirecting…');
        setTimeout(() => navigate('/login'), 2500);
      }
    };

    finish();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50 flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="mx-auto bg-primary text-primary-foreground w-16 h-16 rounded-full flex items-center justify-center">
          <Store className="h-8 w-8" />
        </div>
        <p className="text-muted-foreground text-sm">{status}</p>
      </div>
    </div>
  );
}
